<script setup lang="ts">
import type { SelfExclusionPayload } from '@/types/UserLimits';

const { $containment } = useNuxtApp();
const { t } = useTexts();
const { setCurrentMenuOpenByGroup } = useAccountMenuStore();
const { openToaster } = useLayoutStore();
const { getDateTime } = useDateTimeHelper();
const { fetchUserProfile } = useUserData();

useSeo({
  title: t('account.self_exclusion.title'),
});

const {
  userLimits,
  setSelfExclusion,
  userExclusionMinOptionVal,
  userIsOnExclusionPermanentActive,
} = useUserLimits();

const isLoading = ref<boolean>(false);
const selfExclusionActive = ref<number>(0);
const selfExclusionPeriod = ref<string>('0');
const selfExclusionReason = ref<string>('0');

const choiceOptions = [
  { value: 0, label: t('general.no') },
  { value: 1, label: t('general.yes') },
];

const exclusionOptions = [
  {
    value: '0',
    label: t('account.self_exclusion.select_period'),
    buttonText: t('account.self_exclusion.select_period'),
  },
  {
    value: '3',
    label: `3 ${t('general.months')}`,
    buttonText: `${t('account.self_exclusion_exclude_account', {
      period: `3 ${t('general.months')}`,
    })}`,
  },
  {
    value: '6',
    label: `6 ${t('general.months')}`,
    buttonText: `${t('account.self_exclusion_exclude_account', {
      period: `6 ${t('general.months')}`,
    })}`,
  },
  {
    value: '12',
    label: `1 ${t('general.year')}`,
    buttonText: `${t('account.self_exclusion_exclude_account', {
      period: `1 ${t('general.year')}`,
    })}`,
  },
  {
    value: '24',
    label: `2 ${t('general.years')}`,
    buttonText: `${t('account.self_exclusion_exclude_account', {
      period: `2 ${t('general.years')}`,
    })}`,
  },
  {
    value: '36',
    label: `3 ${t('general.years')}`,
    buttonText: `${t('account.self_exclusion_exclude_account', {
      period: `3 ${t('general.years')}`,
    })}`,
  },
  {
    value: '48',
    label: `4 ${t('general.years')}`,
    buttonText: `${t('account.self_exclusion_exclude_account', {
      period: `4 ${t('general.years')}`,
    })}`,
  },
  {
    value: '60',
    label: `5 ${t('general.years')}`,
    buttonText: `${t('account.self_exclusion_exclude_account', {
      period: `5 ${t('general.years')}`,
    })}`,
  },
  {
    value: '99',
    label: t('account.self_exclusion.permanent'),
    buttonText: t('account.self_exclusion.permanent_button'),
  },
];

const reasonOptions = [
  {
    value: '0',
    label: t('account.self_exclusion.reason_0'),
    alternative: false,
  },
  {
    value: '1',
    label: t('account.self_exclusion.reason_1'),
    alternative: true,
  },
  {
    value: '2',
    label: t('account.self_exclusion.reason_2'),
    alternative: true,
  },
  {
    value: '3',
    label: t('account.self_exclusion.reason_3'),
    alternative: true,
  },
  {
    value: '4',
    label: t('account.self_exclusion.reason_4'),
    alternative: true,
  },
  {
    value: '5',
    label: t('account.self_exclusion.reason_5'),
    alternative: false,
  },
  {
    value: '6',
    label: t('account.self_exclusion.reason_6'),
    alternative: false,
  },
  {
    value: '7',
    label: t('account.self_exclusion.reason_7'),
    alternative: false,
  },
  {
    value: '8',
    label: t('account.self_exclusion.reason_8'),
    alternative: false,
  },
  {
    value: '9',
    label: t('account.self_exclusion.reason_9'),
    alternative: true,
  },
];

const isContinueAnyway = ref<boolean>(false);

const isReasonWithAlternative = computed<boolean>(() => {
  return (
    reasonOptions.find((option) => {
      return option.value === selfExclusionReason.value;
    })?.alternative || false
  );
});

const exclusionOptionsFiltered = computed(() => {
  return exclusionOptions.filter(
    (option) =>
      Number(option.value) >= Number(userExclusionMinOptionVal.value) ||
      option.value === '0',
  );
});

async function handleSubmit() {
  if (selfExclusionPeriod.value === '0' || !selfExclusionPeriod.value) {
    openToaster({
      message: t('account.self_exclusion.select_period'),
      type: 'error',
      time: 10000,
    });

    return;
  }

  if (selfExclusionReason.value === '0' || !selfExclusionReason.value) {
    openToaster({
      message: t('account.self_exclusion.reason_0'),
      type: 'error',
      time: 10000,
    });
  }

  await $containment.handle('update_limits', () => {
    sendSubmit();
  });
}

async function sendSubmit() {
  if (!selfExclusionPeriod.value) {
    return;
  }

  if (!selfExclusionReason.value) {
    return;
  }

  const { $kyc } = useNuxtApp();
  const { temporaryPassword } = useModulesValidation();

  isLoading.value = true;

  const payload: SelfExclusionPayload = {
    user_exclusion: Number(selfExclusionPeriod.value),
    reason: Number(selfExclusionReason.value),
  };

  if ($kyc?.current?.id) {
    payload.kyc_id = $kyc.current.id;
  }

  if (temporaryPassword.get()) {
    payload.password_check = temporaryPassword.get();
  }

  await setSelfExclusion(payload)
    .then((res) => {
      const message =
        res.data?.message || t('account.timeout_limits.update_success');

      openToaster({
        message,
        type: 'success',
      });
    })
    .catch((error) => {
      const message =
        error.data?.message ||
        error.message ||
        t('account.timeout_limits.generic_error');

      openToaster({
        message,
        type: 'error',
        time: 12000,
      });
    })
    .finally(() => {
      isLoading.value = false;
      temporaryPassword.clear();
    });
}

const isDisabledRemoveExclusion = computed(() => {
  if (userLimits.value?.user_exclusion) {
    return true;
  }

  return false;
});

const isDisabledExclusionButton = computed(() => {
  if (
    isLoading.value ||
    !selfExclusionPeriod.value ||
    !selfExclusionReason.value ||
    selfExclusionPeriod.value === '0' ||
    selfExclusionReason.value === '0'
  ) {
    return true;
  }

  return false;
});

function getExclusionButtonText() {
  if (selfExclusionPeriod.value === '0') {
    return t('account.self_exclusion.select_period');
  }

  if (selfExclusionReason.value === '0') {
    return t('account.self_exclusion.reason_0');
  }

  return exclusionOptions.find(
    (option) => option.value === selfExclusionPeriod.value,
  )?.buttonText;
}

onBeforeMount(async () => {
  if (!userLimits.value?.user_exclusion_at) {
    await fetchUserProfile({ force: true });
  }
});

onMounted(() => {
  setCurrentMenuOpenByGroup('user-account');
});

watch(
  userLimits,
  () => {
    if (userLimits.value?.user_exclusion) {
      selfExclusionActive.value = 1;
      selfExclusionPeriod.value = String(userLimits.value?.user_exclusion);
    } else {
      selfExclusionActive.value = 0;
    }
  },
  {
    immediate: true,
  },
);

watch(selfExclusionReason, (newValue, oldValue) => {
  if (newValue !== oldValue) {
    isContinueAnyway.value = false;
  }
});
</script>

<template>
  <BaseLoading
    class="!bg-auth-bg-primary !absolute"
    :icon-loader="true"
    :show="isLoading"
  />

  <div
    v-if="!userIsOnExclusionPermanentActive"
    :class="$style.formContainer"
  >
    <div :class="$style.groupRadio">
      <label>
        {{ $t('account.self_exclusion.set_exclusion') }}
      </label>

      <BaseFormRadio
        v-model="selfExclusionActive"
        :disabled="isDisabledRemoveExclusion"
        :options="choiceOptions"
      />
    </div>

    <template v-if="!!selfExclusionActive">
      <div :class="$style.formGroup">
        <label for="dailyLimit">
          {{ $t('account.self_exclusion.period') }}
        </label>
        <BaseFormSelectField
          id="fieldPeriod"
          v-model="selfExclusionPeriod"
          :initial-value="selfExclusionPeriod"
          name="fieldPeriod"
          :options="exclusionOptionsFiltered"
        />
      </div>

      <div :class="$style.formGroup">
        <label for="fieldReason">
          {{ $t('account.self_exclusion.reason') }}
        </label>
        <BaseFormSelectField
          id="fieldReason"
          v-model="selfExclusionReason"
          :initial-value="selfExclusionReason"
          name="fieldReason"
          :options="reasonOptions"
        />
      </div>
    </template>

    <div
      v-if="userLimits?.user_exclusion"
      :class="$style.lastChange"
    >
      {{ $t('account.account_protection.last_change') }}
      <strong>
        {{ getDateTime(userLimits?.user_exclusion_at) }}
      </strong>
    </div>

    <template v-if="!userIsOnExclusionPermanentActive && !!selfExclusionActive">
      <UserAccountProtectionExclusionAlternatives
        v-if="isReasonWithAlternative && !isContinueAnyway"
        @continue="isContinueAnyway = true"
      />

      <div
        v-else
        class="flex justify-between items-end w-full col-span-2"
      >
        <button
          :class="$style.btnCancelAccount"
          :disabled="isDisabledExclusionButton"
          @click="handleSubmit"
        >
          <NuxtIcon name="close" />
          {{ getExclusionButtonText() }}
        </button>
      </div>
    </template>
  </div>
</template>

<style module lang="scss">
.formContainer {
  @apply w-full flex flex-col gap-4 mt-5 pt-5 border-t border-texts/10 border-solid;

  .groupRadio {
    @apply flex items-center gap-5 font-semibold text-xs md:text-sm text-texts;
  }

  .formGroup {
    @apply relative;

    label {
      @apply text-xs font-medium;
    }
  }

  .lastChange {
    @apply text-2xs text-warning/70;
  }

  .btnCancelAccount {
    @apply py-2 px-4 rounded-md text-sm font-semibold whitespace-nowrap flex items-center justify-center gap-2 bg-bg-primary/80 border-auth-bg-inputs border border-solid text-error;

    &[disabled]:not([disabled='false']) {
      @apply opacity-40 cursor-not-allowed;
    }
  }
}
</style>
